import React from "react";
import { useForm, useFieldArray } from "react-hook-form";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { Label } from "../../../components/ui/label";
import TimePicker from "../../../components/ui/timepicker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Plus, Trash } from "lucide-react";
import { DatePicker } from "../../../components/ui/datepicker";
import { Calender, DebitCredit, Notes } from "../../../assets";
import { Textarea } from "../../../components/ui/textarea";

interface AccountEntry {
  account: string;
  amount: string;
}

interface FormData {
  date: Date | null;
  time: string;
  debitAccounts: AccountEntry[];
  creditAccounts: AccountEntry[];
  description: string;
}

interface FieldErrors {
  debitAccounts?: {
    [key: number]: {
      amount?: { message?: string };
      account?: { message?: string };
    };
  };
  creditAccounts?: {
    [key: number]: {
      amount?: { message?: string };
      account?: { message?: string };
    };
  };
  date?: { message?: string };
  time?: { message?: string };
  description?: { message?: string };
}

interface AddEntryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  step: number;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  handleNext: () => void;
  handlePrev: () => void;
}

const getStepIcon = (index: number, step: number) => {
  let strokeColor = "#2865D0";

  if (index < step) {
    // Completed step: fill with white color and stroke white
    strokeColor = "#1447e6";
  } else if (index === step) {
    // Current step: blue stroke color
    strokeColor = "#1447e6";
  } else {
    // Default step: blue stroke color
    strokeColor = "#2865D0";
  }

  if (index < step) {
    return "✓";
  } else if (index === 0) {
    return <Calender strokeColor={strokeColor} />;
  } else if (index === 1) {
    return <DebitCredit strokeColor={strokeColor} />;
  } else if (index === 2) {
    return <DebitCredit strokeColor={strokeColor} />;
  } else if (index == 3) {
    return <Notes strokeColor={strokeColor} />;
  } else {
    return "•";
  }
};

const AddEntryDialog = ({
  open,
  onOpenChange,
  setIsModalOpen,
  step,
  setStep,
  handleNext,
  handlePrev,
}: AddEntryDialogProps) => {
  const steps = ["Effective Date", "Debit Account", "Credit Account", "Notes"];

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    trigger,
    clearErrors,
  } = useForm<FormData>({
    defaultValues: {
      date: new Date(),
      time: "07:00 AM",
      debitAccounts: [{ account: "", amount: "" }],
      creditAccounts: [{ account: "", amount: "" }],
      description: "",
    },
    mode: "onChange",
  });

  const {
    fields: debitFields,
    append: appendDebit,
    remove: removeDebit,
  } = useFieldArray({
    control,
    name: "debitAccounts",
  });

  const {
    fields: creditFields,
    append: appendCredit,
    remove: removeCredit,
  } = useFieldArray({
    control,
    name: "creditAccounts",
  });

  const onSubmit = (data: FormData) => {
    setIsModalOpen(false);
  };

  const handleAddEntry = async (type: "debit" | "credit") => {
    const currentFields = type === "debit" ? debitFields : creditFields;
    const fieldName = type === "debit" ? "debitAccounts" : "creditAccounts";

    // Validate current row before adding new one
    const isValid = await trigger(`${fieldName}.${currentFields.length - 1}`);

    if (isValid) {
      if (type === "debit") {
        appendDebit({ account: "", amount: "" });
      } else {
        appendCredit({ account: "", amount: "" });
      }
    }
  };

  const handleNextStep = async () => {
    let isValid = false;

    if (step === 0) {
      const currentDate = watch("date");

      // Validate both date and time
      if (!currentDate) {
        setValue("date", null, { shouldValidate: true });
      }
      setValue("time", "", { shouldValidate: true });

      isValid = await trigger(["date", "time"]);
    } else if (step === 1) {
      isValid = await trigger("debitAccounts");
    } else if (step === 2) {
      isValid = await trigger("creditAccounts");
    } else if (step === 3) {
      isValid = await trigger("description");
    }

    if (isValid) {
      handleNext();
    }
  };

  const handlePrevStep = () => {
    // Clear errors when going back
    clearErrors();
    handlePrev();
  };

  const getAllErrors = () => {
    const fieldErrors = errors as FieldErrors;
    const errorMessages: string[] = [];

    if (step === 0) {
      if (fieldErrors.date?.message) errorMessages.push("Date is required");
      if (fieldErrors.time?.message) errorMessages.push("Time is required");
    } else if (step === 1) {
      const debitErrors = fieldErrors.debitAccounts;
      if (debitErrors) {
        Object.entries(debitErrors).forEach(([index, account]) => {
          if (account?.amount?.message)
            errorMessages.push(
              `Debit ${Number(index) + 1}: ${account.amount.message}`
            );
          if (account?.account?.message)
            errorMessages.push(
              `Debit ${Number(index) + 1}: ${account.account.message}`
            );
        });
      }
    } else if (step === 2) {
      const creditErrors = fieldErrors.creditAccounts;
      if (creditErrors) {
        Object.entries(creditErrors).forEach(([index, account]) => {
          if (account?.amount?.message)
            errorMessages.push(
              `Credit ${Number(index) + 1}: ${account.amount.message}`
            );
          if (account?.account?.message)
            errorMessages.push(
              `Credit ${Number(index) + 1}: ${account.account.message}`
            );
        });
      }
    } else if (step === 3) {
      if (fieldErrors.description?.message)
        errorMessages.push(fieldErrors.description.message);
    }

    return errorMessages;
  };

  const handleAccountChange = (
    value: string,
    type: "debit" | "credit",
    index: number
  ) => {
    const fieldName =
      type === "debit"
        ? (`debitAccounts.${index}.account` as const)
        : (`creditAccounts.${index}.account` as const);
    setValue(fieldName, value, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  const handleDateChange = (date: Date | undefined) => {
    setValue("date", date || null, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  const handleTimeChange = (time: string) => {
    // Validate that time is not 00:00:00
    setValue("time", time);
  };

  const [datePickerOpen, setDatePickerOpen] = React.useState(false);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest(".date-picker-container")) {
        setDatePickerOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full max-w-4xl p-0 gap-0">
        <DialogHeader className="px-6 py-4">
          <DialogTitle className="BookEZ_font_body_large BookEZ_font_bold BookEZ_font_color_gray_2">
            Add New Entry
          </DialogTitle>
        </DialogHeader>
        {/* Stepper */}
        <div className="px-6 py-6">
          <div className="relative flex justify-between items-center">
            <div className="absolute top-5 left-0 right-0 h-[2px] bg-gray-200 z-0" />
            {steps.map((label, index) => (
              <div
                key={index}
                className="relative z-10 flex flex-col items-center"
              >
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center BookEZ_font_body_size mb-2 
                  ${
                    index <= step
                      ? "BookEZ_add_entry_active_bg BookEZ_add_entry_active_font_color"
                      : "BookEZ_add_entry_bg BookEZ_add_entry_font_color"
                  }
                  transition-all duration-200
                `}
                >
                  {index < step ? (
                    <span className="BookEZ_add_entry_active_icon_color">
                      ✓
                    </span>
                  ) : (
                    getStepIcon(index, step)
                  )}
                </div>
                <p
                  className={`BookEZ_font_body_size BookEZ_font_body_medium whitespace-nowrap 
                ${
                  index <= step
                    ? "BookEZ_add_entry_active_font_color"
                    : "BookEZ_font_color_gray_2"
                }`}
                >
                  {label}
                </p>
              </div>
            ))}
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="p-6">
            {/* Step 0 */}
            {step === 0 && (
              <div className="flex gap-6">
                <div className="w-full">
                  <Label className="BookEZ_font_body_size BookEZ_font_color_gray_2 mb-2 block">
                    Select Date
                  </Label>
                  <DatePicker
                    label="Select Date"
                    date={watch("date") || undefined}
                    onDateChange={handleDateChange}
                  />
                </div>
                <div className="w-full">
                  <TimePicker
                    time={watch("time")}
                    setTime={handleTimeChange}
                    label="Select Time"
                    error={!!errors.time}
                  />
                </div>
              </div>
            )}

            {/* Step 1 and 2 */}
            {(step === 1 || step === 2) && (
              <div className="flex flex-col gap-4">
                {(step === 1 ? debitFields : creditFields).map(
                  (field, index) => (
                    <div
                      key={field.id}
                      className="BookEZ_card_bg p-5 rounded-lg border border-gray-200 shadow-sm"
                    >
                      <div className="flex gap-4">
                        <div className="flex-1">
                          <Label className="BookEZ_font_body_size  BookEZ_font_body_medium BookEZ_font_color_gray_2 mb-2 block">
                            Amount
                          </Label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 BookEZ_font_color_gray_2">
                              $
                            </span>
                            <Input
                              {...register(
                                step === 1
                                  ? `debitAccounts.${index}.amount`
                                  : `creditAccounts.${index}.amount`,
                                {
                                  required: "Amount is required",
                                  validate: (value) =>
                                    value !== "" || "Amount is required",
                                }
                              )}
                              type="number"
                              placeholder="0.00"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs BookEZ_font_body_medium px-2 py-1 BookEZ_bg_blue_light BookEZ_font_color_blue rounded">
                              {step === 1 ? "Debit" : "Credit"}
                            </span>
                          </div>
                        </div>

                        <div className="flex-1">
                          <Label className="BookEZ_font_body_size BookEZ_font_body_medium BookEZ_font_color_gray_2 mb-2 block">
                            Account Name
                          </Label>
                          <Select
                            value={watch(
                              step === 1
                                ? `debitAccounts.${index}.account`
                                : `creditAccounts.${index}.account`
                            )}
                            onValueChange={(value) =>
                              handleAccountChange(
                                value,
                                step === 1 ? "debit" : "credit",
                                index
                              )
                            }
                          >
                            <SelectTrigger className={`w-full`}>
                              <SelectValue
                                className="BookEZ_font_color_gray_2"
                                placeholder="Select Account"
                              />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="cash">Cash</SelectItem>
                              <SelectItem value="bank">Bank</SelectItem>
                              <SelectItem value="receivables">
                                Accounts Receivable
                              </SelectItem>
                              <SelectItem value="payables">
                                Accounts Payable
                              </SelectItem>
                            </SelectContent>
                          </Select>
                          <input
                            type="hidden"
                            {...register(
                              step === 1
                                ? `debitAccounts.${index}.account`
                                : `creditAccounts.${index}.account`,
                              {
                                required: "Account name is required",
                                validate: (value) =>
                                  value !== "" || "Account name is required",
                              }
                            )}
                          />
                        </div>

                        {(step === 1 ? debitFields : creditFields).length >
                          1 && (
                          <div className="flex items-end">
                            <Button
                              type="button"
                              className="border border-red-500 BookEZ_error_transparent hover:bg-red-400"
                              onClick={() =>
                                step === 1
                                  ? removeDebit(index)
                                  : removeCredit(index)
                              }
                            >
                              <Trash className="h-4 w-4" color="#d73b38" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  )
                )}

                <Button
                  type="button"
                  variant="outlineBlack"
                  className="mt-2 flex items-center w-1/3 gap-2 border-dashed"
                  onClick={() =>
                    handleAddEntry(step === 1 ? "debit" : "credit")
                  }
                >
                  <Plus
                    className="h-4 w-4 BookEZ_btn_outlined_font_color"
                    color="currentColor"
                  />
                  Add {step === 1 ? "Debit" : "Credit"} Entry
                </Button>
              </div>
            )}

            {/* Step 3 */}
            {step === 3 && (
              <div className="w-full">
                <Label className="BookEZ_font_body_size BookEZ_font_body_medium BookEZ_font_color_gray_2 mb-2 block">
                  Description
                </Label>
                <Textarea
                  {...register("description", {
                    required: "Description is required",
                    validate: (value) =>
                      value.trim() !== "" || "Description is required",
                  })}
                  placeholder="Enter description"
                  className={"h-9"}
                  onBlur={() => trigger("description")}
                />
              </div>
            )}

            {/* Error Box - Only show if there are errors and the current step has been touched */}
            {Object.keys(errors).length > 0 && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-red-400"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="BookEZ_font_body_size  BookEZ_font_body_medium text-red-800">
                      Please fix the following errors:
                    </h3>
                    <div className="mt-2 BookEZ_font_body_size  text-red-700">
                      <ul className="list-disc pl-5 space-y-1">
                        {getAllErrors().map((error, index) => (
                          <li key={index}>{error}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-between items-center px-6 py-4">
            <Button
              type="button"
              variant="outline"
              disabled={step === 0}
              onClick={handlePrevStep}
              className="px-4 py-2"
            >
              ← Back
            </Button>
            <Button
              type={step === steps.length - 1 ? "submit" : "button"}
              variant="filled"
              onClick={step === steps.length - 1 ? undefined : handleNextStep}
              className="px-4 py-2"
            >
              {step === steps.length - 1 ? "Submit" : "Next →"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddEntryDialog;
